import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  isDevMode,
  OnDestroy,
  OnInit,
  Optional,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { SimCellDefDirective, SimColumnDefDirective, SimHeaderCellDefDirective } from './cell';
import { SIM_TEXT_COLUMN_OPTIONS, TextColumnOptions } from './default-options';
import { SimTableComponent } from './table.component';

/**
 * 文本列，它仅显示标题单元格和行单元格的文本内容。假设该表使用本机表实现('<table>')。
 *
 * - 默认情况下，此列的名称为标题文本和数据属性访问器。
 * - 标题文本可以被“headerText”输入覆盖。
 * - 单元格值可以用“dataAccessor”输入覆盖。
 * - 使用“justify”输入将文本的对齐更改为'left'或'right'。参考css属性'text-align'
 */
@Component({
  selector: 'sim-text-column',
  template: `
    <ng-container simColumnDef>
      <th sim-header-cell *simHeaderCellDef [style.text-align]="justify">
        {{ headerText }}
      </th>
      <td sim-cell *simCellDef="let data" [style.text-align]="justify">
        {{ dataAccessor(data, name) }}
      </td>
    </ng-container>
  `,
  encapsulation: ViewEncapsulation.None,
  // 有意不将更改检测设置为OnPush。将提供该组件的模板到要插入到其视图中的表。
  // 这在以后运行变更检测时是有问题的这个模板中的绑定将在表的视图被计算之后进行计算在表的视图中的模板将没有更新的值(实际上将导致一个ExpressionChangedAfterItHasBeenCheckedError)。
  changeDetection: ChangeDetectionStrategy.Default
})
export class SimTextColumnComponent<T> implements OnDestroy, OnInit {
  /** 用于引用此列的列名 */
  @Input()
  get name(): string {
    return this._name;
  }
  set name(name: string) {
    this._name = name;

    // 使用Ivy，可以在静态查询结果可用之前初始化输入。在这种情况下，我们延迟同步直到“ngOnInit”触发。
    this._syncColumnDefName();
  }
  _name: string;

  /**
   * 应用于列标题的文本标签。
   */
  @Input() headerText: string;

  /**
   * 访问器函数检索为每个单元格呈现的数据。
   * 如果未设置此属性，则数据单元格将呈现在数据属性中找到的与列名称匹配的值。
   * 例如，如果列名为'id'，则呈现的值将由数据的'id'属性定义。
   */
  @Input() dataAccessor: (data: T, name: string) => string;

  /** 单元格值的对齐 */
  @Input() justify: 'start' | 'end' = 'start';

  /**
   * columnDef在'ngOnInit'期间通过静态查询提供。
   */
  @ViewChild(SimColumnDefDirective, { static: true }) columnDef: SimColumnDefDirective;

  /**
   * cell在'ngOnInit'期间通过静态查询提供给columnDef。
   */
  @ViewChild(SimCellDefDirective, { static: true }) cell: SimCellDefDirective;

  /**
   * headerCell在'ngOnInit'期间通过静态查询提供给columnDef。
   */
  @ViewChild(SimHeaderCellDefDirective, { static: true }) headerCell: SimHeaderCellDefDirective;

  constructor(
    @Optional() private _table: SimTableComponent<T>,
    @Optional() @Inject(SIM_TEXT_COLUMN_OPTIONS) private _options: TextColumnOptions<T>
  ) {
    this._options = _options || {};
  }

  ngOnInit() {
    this._syncColumnDefName();

    if (this.headerText === undefined) {
      this.headerText = this._createDefaultHeaderText();
    }

    if (!this.dataAccessor) {
      this.dataAccessor = this._options.defaultDataAccessor || ((data: T, name: string) => data[name]);
    }

    if (this._table) {
      // 使用静态的'ViewChild'查询直接向表提供'cell`和'headerCell`，因为在表检查其内容并初始化行时，'columnDef'将不会获取其内容。
      this.columnDef.cell = this.cell;
      this.columnDef.headerCell = this.headerCell;
      this._table.addColumnDef(this.columnDef);
    } else {
      throw Error(`Text column could not find a parent table for registration.`);
    }
  }

  ngOnDestroy() {
    if (this._table) {
      this._table.removeColumnDef(this.columnDef);
    }
  }

  /**
   * 创建一个默认的标题文本。
   */
  _createDefaultHeaderText() {
    const name = this.name;

    if (isDevMode() && !name) {
      throw Error(`Table text column must have a name.`);
    }

    if (this._options && this._options.defaultHeaderTextTransform) {
      return this._options.defaultHeaderTextTransform(name);
    }

    return name[0].toUpperCase() + name.slice(1);
  }

  /** 使列定义名称与文本列名同步 */
  private _syncColumnDefName() {
    if (this.columnDef) {
      this.columnDef.name = this.name;
    }
  }
}
